import React from 'react';

import { Button, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import type { Breakpoint } from '@mui/material';

import { useBoolean, useDebounce } from '@looko-corp/acloset-react-kit/src/react';

import { LookoButton } from '../Button/LookoButton';

export interface EventAlertDialogProps {
  /**
   * 알림을 열거나 닫습니다.
   */
  open: boolean;

  /**
   * 알림 제목.
   */
  title?: string;

  /**
   * 알림 제목 색상.
   */
  titleColor?: React.ComponentProps<typeof Typography>['color'];

  /**
   * 알림 내용.
   */
  content?: React.ReactNode;

  /**
   * 확인 버튼 텍스트.
   */
  confirmButtonText?: string;

  /**
   * 확인 버튼 텍스트 색상.
   */
  confirmButtonColor?: React.ComponentProps<typeof LookoButton>['color'];

  /**
   * 거절 버튼 텍스트.
   *
   * undefined 일 경우, 버튼을 그리지 않습니다.
   */
  rejectButtonText?: string;

  /**
   * 거절 버튼 텍스트 색상.
   */
  rejectButtonColor?: React.ComponentProps<typeof LookoButton>['color'];

  /**
   * 취소 버튼 텍스트.
   */
  cancelButtonText?: string;

  /**
   * escape 버튼 또는 백드롭 클릭으로 인한 알림 닫기 요청을 비활성화 합니다.
   */
  disableEscape?: boolean;

  /**
   * 확인 버튼 이벤트.
   *
   * True 시에 알림을 닫습니다.
   */
  onConfirm?: () => boolean | Promise<boolean>;

  /**
   * 거절 버튼 이벤트.
   *
   * True 시에 알림을 닫습니다.
   */
  onReject?: () => boolean | Promise<boolean>;

  /**
   * 취소 버튼 이벤트.
   */
  onCancel?: () => boolean | Promise<boolean>;

  /**
   * 알림 닫기를 요청할 때 불립니다.
   */
  onClose?: () => void;

  maxWidth?: false | Breakpoint | undefined;

  fullWidth?: boolean;
}

/**
 * 이벤트 알림 다이얼로그. `open` prop으로 표시 여부를 제어.
 *
 * ```tsx
 * const { value: open, setValue: setOpen } = useBoolean(false);
 *
 * <Button onClick={() => setOpen(true)}>알림 열기</Button>
 * <EventAlertDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   title="알림 제목"
 *   content="알림 내용"
 *   onConfirm={() => true}
 * />
 * ```
 */
export const EventAlertDialog = (props: EventAlertDialogProps) => {
  const { value: isHandling, setValue: setHandling } = useBoolean(false);

  const handleCallback = useDebounce(async () => {
    setHandling(true);
    try {
      if (props.onConfirm) {
        await props.onConfirm();
      }
    } finally {
      setHandling(false);
      props.onClose?.();
    }
  });

  const onReject = useDebounce(async () => {
    setHandling(true);
    try {
      if (props.onReject ? await props.onReject() : true) {
        props.onClose?.();
      }
    } finally {
      setHandling(false);
    }
  });

  const onCancel = useDebounce(async () => {
    setHandling(true);
    try {
      if (props.onCancel ? await props.onCancel() : true) {
        props.onClose?.();
      }
    } finally {
      setHandling(false);
    }
  });

  React.useLayoutEffect(() => {
    if (!props.open) {
      setHandling(false);
    }
  }, [props.open, setHandling]);

  return (
    <Dialog
      open={props.open}
      onClose={props.disableEscape ? undefined : props.onClose}
      fullWidth={props.fullWidth ?? true}
      maxWidth={props.maxWidth ?? 'xs'}
    >
      <DialogContent>
        <Stack gap={1}>
          {props.title != null && (
            <Typography variant="Title_Looko" style={{ whiteSpace: 'pre-line' }} color={props.titleColor}>
              {props.title}
            </Typography>
          )}
          {props.content != null &&
            (typeof props.content === 'string' ? (
              <Typography variant="Body_Looko">{props.content}</Typography>
            ) : (
              props.content
            ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pt: 0 }}>
        <LookoButton
          variant="text"
          loading={isHandling}
          onClick={handleCallback}
          autoFocus
          color={props.confirmButtonColor}
        >
          {props.confirmButtonText ?? '확인'}
        </LookoButton>

        {props.rejectButtonText != null && (
          <Button
            variant="text"
            // loading={isHandling}
            onClick={onReject}
            autoFocus
            color={props.rejectButtonColor}
            sx={props.rejectButtonColor == null ? { color: 'grey.500' } : undefined}
          >
            {props.rejectButtonText}
          </Button>
        )}

        {props.cancelButtonText != null && (
          <Button onClick={onCancel} sx={{ color: 'grey.500' }}>
            {props.cancelButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
